import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import Button from "./ui/Button";
import { useViewer, type ViewerOrganization } from "../services/viewer";
import { provisionMyFamily } from "../services/household";

/**
 * Says where this person's sensors actually are, when their personal account has
 * nothing on it.
 *
 * **What this replaced, and why it had to go.** `OrganizationGate` used to sit here.
 * It asked "Which organization?" after sign-in, and for a single organization it
 * redirected straight back through Auth0 without asking. Its premise was that Auth0
 * offered nothing usable -- its own comment said Auth0's prompt "asks the person to
 * type the organization's slug, which nobody knows".
 *
 * **That stopped being true.** Auth0's login now shows *Choose an Account to
 * Continue*: the personal account, then every organization the person belongs to, by
 * display name and logo. It runs before the token is minted. So the gate was asking a
 * question that had already been answered, one screen later, with less information --
 * and it did three things that were actively wrong:
 *
 * - it **overrode the answer**. Somebody who clicked "Continue with personal account"
 *   was immediately sent back to sign in as an organization, so the personal option
 *   could not be reached through the dashboard at all;
 * - it asked **again**, without the logos and without offering personal;
 * - with one organization it produced a **false error**. The first personal sign-in
 *   redirected, and a second one in the same browser session hit the "already tried"
 *   record and landed on *"We couldn't sign you in to X"* -- when nothing had failed
 *   and the person had simply chosen personal.
 *
 * **What is still worth catching.** Choosing the personal account is only sensible if
 * there is a home behind it. The people this screen is for are overwhelmingly the ones
 * with nothing there: a resident whose building manager invited them, a care
 * provider's staff. Their sensors are with the organization and "personal" means
 * nothing to them -- and the invitation that brought them in carried an `organization`
 * parameter, so this is the first time they have ever been asked to choose at all.
 *
 * **So it leads with where their sensors are, not with what they got wrong.** The
 * primary action goes straight to the organization rather than back to Auth0's chooser:
 * they have already answered one question they did not expect, and sending them to
 * answer it again is not a recovery. Setting up a home of their own is offered second,
 * for the case that genuinely needs it -- somebody installing a sensor they bought
 * themselves (REQ-TEN-3's `own_home_requested`).
 *
 * **Every sentence has to hold at every tier.** The copy cannot be varied by what
 * they are in the organization, because this screen cannot find out:
 * `ViewerOrganization` carries an id and a display name, and the roles on this viewer
 * are the personal login's -- an organization's scoped roles exist only on a token
 * minted with organization context. Promising "properties, sensors and people" was
 * true for an `OrgAdmin` and false for an `Organization Monitor`, who sees the sensors
 * their grant names and no People page at all.
 *
 * **Not a gate.** It never redirects on its own and decides nothing. With a home, or
 * with no organizations, it renders its children and gets out of the way -- the
 * household case belongs to `HouseholdGate` below, which provisions one, and which
 * refuses to do so for an organization member on purpose.
 */
export default function PersonalAccountNotice({
  children,
}: {
  children: React.ReactNode;
}) {
  const { loginWithRedirect } = useAuth0();
  const { data: viewer, isPending, isError } = useViewer();
  const [state, setState] = useState<"idle" | "working" | "failed">("idle");

  // The viewer gates everything below and a failure to read it is handled where it
  // already is; this only decides about one state.
  if (isPending || isError || !viewer) return <>{children}</>;

  const organizations = viewer.organizations ?? [];

  // No tenant, but somewhere to go. Somebody with no tenant AND no organizations is
  // a consumer who needs a household, which is HouseholdGate's business.
  const strandedOnPersonal = !viewer.tenant_id && organizations.length > 0;

  if (!strandedOnPersonal) return <>{children}</>;

  const enter = (organization: ViewerOrganization) =>
    loginWithRedirect({
      authorizationParams: { organization: organization.id },
    });

  /**
   * Asks for a home of their own, then signs in again to pick up the claim.
   *
   * `own_home_requested` has to be set deliberately -- it is the whole of what
   * replaced REQ-TEN-3's outright refusal of an organization's member, so no client
   * may default it. This is the deliberate act: a second, quieter button on a screen
   * whose loud one goes somewhere else.
   */
  const setUpOwnHome = async () => {
    setState("working");

    try {
      await provisionMyFamily({ ownHomeRequested: true });

      // The tenant claim is Auth0-signed and this browser's token was minted before
      // the household existed, so nothing the API returns can change it. A full
      // sign-in, not a silent renewal, for the reason HouseholdGate records: the
      // post-login Action is not known to run on a refresh-token exchange.
      await loginWithRedirect();
    } catch {
      // Reported rather than retried. Nothing was spent -- the server either wrote a
      // household or it did not -- so trying again is the remedy, and claiming
      // success here would leave them looking at a dashboard with no tenant.
      setState("failed");
    }
  };

  const only = organizations.length === 1 ? organizations[0] : null;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 text-white">
      <div className="w-full max-w-md text-center space-y-4">
        {/*
          THE SITUATION FIRST, THEN THE WAY OUT. This led with "You have access
          through Good Shepherd", which is the resolution -- and arriving as the
          first line it explains nothing, because the reader does not yet know why
          they are looking at this screen instead of their dashboard. The first
          thing they read has to answer "why am I here", and only then "what do I
          do". The organization's name is the answer to the second question.
        */}
        <h1 className="text-xl font-semibold">
          There are no sensors on your personal account
        </h1>

        {/*
          PROMISES NOTHING A USER CANNOT HAVE. This said "to see its properties,
          sensors and people", which is true only for an OrgAdmin: ManagePeople is
          an administrator's capability, and an Organization Monitor sees the
          sensors their grant names rather than the property tree.

          The copy cannot be varied by role, which is the part worth knowing.
          ViewerOrganization carries an id and a display name and nothing else, and
          the roles on this viewer are the PERSONAL login's -- the organization's
          scoped roles exist only on a token minted with organization context, which
          by definition this is not. So the screen has no way to learn what they are
          in there, and every sentence on it has to hold at every tier.

          "Yours are with X" does: an administrator's are, and so is the one a
          user's grant names.

          AND IT IS WRITTEN FOR A RESIDENT, not for us. The people who land here are
          mostly somebody whose building manager or care provider invited them, and
          an earlier version was our model of the world rather than theirs -- "there
          is no home set up on it" in particular, since "home" is our word for a
          tenant and to somebody in a flat their home IS the flat, so it reads as the
          app having lost it.

          "Personal account" is deliberately NOT in that category, and the reason is
          worth keeping: Auth0's own screen immediately before this one says
          "Continue with personal account". It is the phrase they have just clicked,
          so echoing it is continuity rather than jargon. An earlier revision removed
          it on the theory that nobody would recognise the term, which had the order
          of events backwards.
        */}
        <p className="text-ink-3 text-sm">
          {only
            ? `You have access through ${only.display_name || "your organization"}.`
            : "You have access through your organizations. Pick the one you want."}
        </p>

        <div className="space-y-2">
          {organizations.map((o) => (
            <Button
              key={o.id}
              onClick={() => enter(o)}
              className="w-full"
            >
              {only
                ? `Sign in with ${o.display_name || "your organization"}`
                : o.display_name || o.id}
            </Button>
          ))}
        </div>

        <div className="pt-2 border-t border-white/10 space-y-2">
          {/*
            NAMES WHERE THE NEW SENSOR WOULD LIVE, which is the decision being made.
            An earlier version asked "bought a sensor for your own kitchen?", which
            conflates owning one with where it belongs -- somebody can buy a sensor
            and still want it on their employer's account, and somebody can be given
            one and want it on their own. "Outside of X" is the question they are
            actually answering.
          */}
          <p className="text-ink-3 text-xs">
            {only
              ? `Want to install a sensor outside of ${only.display_name || "your organization"}?`
              : "Want to install a sensor outside your organizations?"}{" "}
            You can create a new home.
          </p>

          <button
            type="button"
            disabled={state === "working"}
            onClick={setUpOwnHome}
            className="text-ink-3 text-xs underline hover:text-white transition-colors disabled:opacity-50"
          >
            {state === "working" ? "Creating your home…" : "Create a new home"}
          </button>

          {state === "failed" && (
            <p className="text-xs text-red-300">
              We could not set up your home. Nothing was changed — please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
